import React, { useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { Flipped, Flipper, spring } from 'react-flip-toolkit';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { connect } from 'unistore/full/react';
import actions from './actions';
import NavigationBar from './FigmaComponents/NavigationBar';
import { getColorForGenreUri } from './get-color-for-genre-uri';
import getImagesUrlBySize from './get-images-url-by-size';
import getTypeFromUri from './get-type-from-uri';
import { LoadingIcon } from './LoadingIcon';
import './Playlist.scss';
import PlaylistHeader from './PlaylistHeader';
import { Scroller } from './Scroller';
import TrackRow from './TrackRow';
import TrackRowActionsWrapper from './TrackRowActionsWrapper';
import { useFirstMountState } from './hooks';
import PlaylistHeaderRadioTags from './PlaylistHeaderRadioTags';

// TODO: lift out npv minimize button
// TODO: fix recs positioning in npv

const formatNumber = new Intl.NumberFormat().format;

const onAppear = (el, index) =>
  spring({
    onUpdate: (val) => {
      el.style.opacity = val;
    },
  });

const onExit = (el, index, removeElement) => {
  spring({
    config: { overshootClamping: true },
    onUpdate: (val) => {
      el.style.opacity = 1 - val;
    },
    onComplete: removeElement,
  });

  return () => {
    el.style.opacity = '';
    removeElement();
  };
};

function Playlist(props) {
  const history = useHistory();
  // useWhyDidYouUpdate('Playlist', props);

  const isDynamicPlaylist = useRouteMatch()?.path?.startsWith(
    '/dynamic-playlist'
  );

  const trackUriToScrollIntoView = new URLSearchParams(
    global.location.search.slice(1)
  ).get('trackUri');

  // useEffect(() => {
  //   if (trackUriToScrollIntoView) {
  //   }
  //   trackUriToScrollIntoView;
  // }, [trackUriToScrollIntoView]);

  const {
    playlistId,
    color,
    currentRow,
    error,
    followersTotal,
    numTracks,
    imageUrl,
    isFollowing,
    name: playlistName,
    description,
    ownerId,
    ownerDisplayName,
    ownerImageUrl,
    rows,
    isOwnedByCurrentUser,
    isOwnedBySpotify,
    addedStateByUri,
    playlistUri,
    trackFeedbackByUri,
    recommendedPlaylists,
    isPlaying,
    isActiveContext,
    canvas,
    section,
    onDemand,
    isPreviewPlayerPlaying,
    previewUri,
    sort,
    isSameFeatureIdentifier,
    seeds,
  } = props;
  // Proxy for being loaded :-S
  useEffect(() => {
    if (!ownerDisplayName) {
      window.scrollTo({ top: (window.innerWidth / 375) * 68 });
    }
  }, [playlistId]);

  useEffect(() => {
    document.body.classList.add('view-playlist');
    return () => {
      document.body.classList.remove('view-playlist');
    };
  }, []);

  useEffect(() => {
    if (isDynamicPlaylist) {
      if (!seeds) props.fetchDynamicPlaylist(props.playlistId);
    } else props.fetchPlaylist(props.playlistId, false);

    let animationFrame = requestAnimationFrame(() => {
      if (window.pageYOffset === 0) {
        animationFrame = requestAnimationFrame(() => {
          if (window.pageYOffset === 0) {
            window.scrollTo({ top: (window.innerWidth / 375) * 68 });
          }
        });
      }
    });

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [playlistId]);

  const onClickPlayButton = useCallback(() => {
    isActiveContext && isSameFeatureIdentifier
      ? props.togglePlayback()
      : props.playTracksFromUri({
          rows,
          shuffle: isDynamicPlaylist ? undefined : true,
          contextUri: playlistUri,
        });
  }, [
    rows,
    playlistUri,
    isActiveContext,
    isSameFeatureIdentifier,
    isDynamicPlaylist,
  ]);

  const [isSortFilterPaneVisible, setIsSortFilterPaneVisible] = useState(false);

  const enhancedArtists = [];
  const artistImagesById = {};

  const tuningSummaryLabel = enhancedArtists
    ?.reduce(
      (out, artist) => {
        if (out.length + artist?.name?.length <= 16) {
          return {
            length: out.length + artist?.name?.length,
            labels: out.labels.concat(artist?.name),
          };
        } else return out;
      },
      { length: 0, labels: [] }
    )
    ?.labels?.map((label) => (
      <>
        <span className="PlaylistHeader__tuning-text-label">{label}</span>{' '}
        <span className="PlaylistHeader__tuning-text-bullet"> • </span>
      </>
    ));

  const rowsAddedByUser = rows.filter(
    (row) =>
      typeof row?.metadata?.source !== 'string' ||
      row?.metadata?.source === 'track-seeds'
  );
  const rowsAddedBySpotify = rows.filter(
    (row) =>
      typeof row?.metadata?.source === 'string' &&
      row?.metadata?.source !== 'track-seeds'
  );

  const [scroller, setScroller] = useState(null);
  const isFirstMount = useFirstMountState();

  const [isPreviewSongsPaneVisible, setIsPreviewSongsPaneVisible] = useState(
    false
  );

  return (
    <>
      <Scroller
        sectionId={`playlist-${playlistId}`}
        scrollId={playlistId}
        className="Playlist__scroller"
        onScroller={setScroller}
      >
        <div
          className={`${
            section === 'tuning' ? 'Playlist__content--tuning' : ''
          }`}
        >
          {!(section === 'tuning' || section === 'tuning-add') && (
            <NavigationBar /*backTo="/"*/ />
          )}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 375 667"
            className="Playlist__sort-filter-pane"
            style={{
              willChange: 'transform',
              transform: `translate3d(0, ${
                isSortFilterPaneVisible ? '0' : '100%'
              }, 0)`,
              transition: 'transform 0.3s ease-in-out',
              position: 'fixed',
              bottom: 0,
              zIndex: 7,
            }}
          >
            <path
              fill="#282828"
              d="M0 4a4 4 0 014-4h367a4 4 0 014 4v703H0V4z"
            ></path>
            <rect
              width="36"
              height="4"
              x="170"
              y="12"
              fill="#616161"
              rx="2"
            ></rect>
            <text
              fill="#fff"
              style={{ whiteSpace: 'pre' }}
              fontSize="24"
              fontWeight="bold"
              letterSpacing="-.04em"
            >
              <tspan x="25" y="61.86">
                Sort by
              </tspan>
            </text>

            {[
              {
                name: 'Custom',
                field: undefined,
              },
              {
                name: 'Title',
                field: 'name',
              },
              { name: 'Artist', field: 'primary_artist_name' },
              { name: 'Album', field: 'album_name' },
              { name: 'Recently Added', field: 'ts_added', dir: 'desc' },
              { name: 'Duration', field: 'duration_ms' },
              { name: 'Release date', field: 'release_date', dir: 'desc' },
              { name: 'Tempo', field: 'tempo', dir: 'desc' },
              { name: 'Relevance', field: 'track_affinity', dir: 'desc' },
              { name: 'Popularity', field: 'popularity', dir: 'desc' },
              // { name: 'Energy', field: 'energy' },
              { name: 'Acousticness', field: 'acousticness', dir: 'desc' },
              { name: 'Danceability', field: 'danceability', dir: 'desc' },
            ].map(({ name, field, dir = 'asc' }, i) => {
              const isActive = field === sort?.name;
              return (
                <g
                  transform={`translate(0, ${i * 44 + 87})`}
                  pointerEvents="bounding-box"
                  onClick={() => {
                    props.fetchPlaylist(playlistId, true, {
                      name: field,
                      asc: dir === 'asc',
                    });
                    setIsSortFilterPaneVisible(false);
                  }}
                >
                  <rect
                    fill="#000"
                    width="375"
                    height="44"
                    fillOpacity="0.001"
                  />
                  <text
                    fill={isActive ? '#1DA64D' : '#fff'}
                    style={{ whiteSpace: 'pre' }}
                    fontSize="16"
                    letterSpacing="0"
                  >
                    <tspan x="24.5" y="23">
                      {name}
                    </tspan>
                  </text>

                  {isActive && (
                    <g transform="translate(0, -422)">
                      <path
                        fill="#1DA64D"
                        fillRule="evenodd"
                        d="M348.848 431.171l-13.827 16.188-5.928-6.94-.657.77 6.585 7.71 14.587-17.079-.76-.649z"
                        clipRule="evenodd"
                      ></path>
                    </g>
                  )}
                </g>
              );
            })}

            <g
              pointerEvents="bounding-box"
              onClick={() => setIsSortFilterPaneVisible(false)}
            >
              <rect
                width="375"
                height="56"
                x="0"
                y="611"
                fill="#000"
                fillOpacity="0.001"
              />
              <text
                fill="#fff"
                fontSize="14"
                letterSpacing="1.4"
                opacity="0.7"
                style={{ whiteSpace: 'pre' }}
                fontWeight="bold"
              >
                <tspan x="155.132" y="638.585">
                  CANCEL
                </tspan>
              </text>
            </g>
          </svg>
          {/* <ConnectedSwipablePlaylistHeader2 playlistId={playlistUri.substr(-22)} /> */}
          {/* <SwipablePlaylistHeader /> */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 375 68"
            style={{
              background: color || 'rgb(96, 96, 96)',
            }}
          >
            <g
              pointerEvents="bounding-box"
              onClick={() =>
                false && setIsSortFilterPaneVisible(!isSortFilterPaneVisible)
              }
            >
              <path
                fill="#fff"
                fillOpacity="0.1"
                d="M288 20a4 4 0 014-4h62a4 4 0 014 4v28a4 4 0 01-4 4h-62a4 4 0 01-4-4V20z"
              ></path>
              <text
                fill="#fff"
                fillOpacity="0.7"
                style={{ whiteSpace: 'pre' }}
                fontSize="13"
                fontWeight="bold"
                letterSpacing="0em"
              >
                <tspan x="304.147" y="38.258">
                  Filters
                </tspan>
              </text>
            </g>

            <path
              fill="#fff"
              fillOpacity="0.1"
              d="M48 24a8 8 0 018-8h216a8 8 0 018 8v20a8 8 0 01-8 8H56a8 8 0 01-8-8V24z"
            ></path>
            <path
              fill="#fff"
              fillOpacity="0.7"
              d="M67.618 37.532a5.589 5.589 0 001.602-3.922 5.61 5.61 0 10-5.61 5.61 5.58 5.58 0 003.246-1.04l2.912 3.409.76-.649-2.91-3.408zm-4.008.688A4.615 4.615 0 0159 33.61 4.615 4.615 0 0163.61 29a4.615 4.615 0 014.61 4.61 4.615 4.615 0 01-4.61 4.61z"
            ></path>
            <text
              fill="#fff"
              fillOpacity="0.7"
              style={{ whiteSpace: 'pre' }}
              fontSize="13"
              fontWeight="bold"
              letterSpacing="0em"
            >
              <tspan x="80" y="38.258">
                Find in playlists
              </tspan>
            </text>
          </svg>
          <div className="Playlist">
            <PlaylistHeader
              title={playlistName}
              description={description}
              subtitle={
                followersTotal != null &&
                `by ${ownerDisplayName} • ${formatNumber(followersTotal)} likes`
              }
              numFollowers={followersTotal}
              numTracks={numTracks}
              color={color}
              ownerImageUrl={ownerImageUrl}
              imageUrl={imageUrl}
              isFollowing={isFollowing}
              onClickPlayButton={onClickPlayButton}
              ownerId={ownerId}
              ownerDisplayName={ownerDisplayName}
              isOwnedByCurrentUser={isOwnedByCurrentUser}
              isOwnedBySpotify={isOwnedBySpotify}
              canvas={canvas}
              isPlaying={isPlaying}
              isActiveContext={isActiveContext}
              isSameFeatureIdentifier={isSameFeatureIdentifier}
              playlistUri={playlistUri}
              playlistId={playlistId}
              seeds={seeds}
              isDynamicPlaylist={isDynamicPlaylist}
            />
            {error && <div>{error}</div>}

            <PlaylistHeaderRadioTags playlistId={playlistId} />

            {isDynamicPlaylist && (
              <Flipper
                flipKey={seeds?.map(({ uri }) => uri).join('')}
                spring="noWobble"
              >
                <div>
                  <div className="Playlist__seeds">
                    {[
                      {
                        id: 'add-more',
                        name: 'Add more',
                      },
                    ]
                      .concat(seeds || [])
                      ?.map(({ id, uri, name, image, onClick = () => {
                          history.push(`/edit/dynamic-playlist/${playlistId}${uri ? `/${getTypeFromUri(uri)}` : ''}`);
                        } }, index) => (
                        <Flipped
                          flipId={`seed-${uri}`}
                          onAppear={
                            !isFirstMount && onAppear
                          } /*onExit={onExit}*/
                        >
                          <svg
                            key={uri}
                            xmlns="http://www.w3.org/2000/svg"
                            width="104"
                            height="152"
                            fill="none"
                            viewBox="0 0 104 152"
                          >
                            <g
                              style={{ pointerEvents: 'bounding-box' }}
                              onClick={onClick}
                            >
                              <g>
                                <circle fill="#242424" cx={52} cy={52} r="52" />

                                {getTypeFromUri(uri) === 'artist' && (
                                  <g transform="translate(26, 26)">
                                    <path
                                      d="M32.88 29.393L30.001 27.738C29.647 27.535 29.544 27.214 29.514 27.042C29.484 26.869 29.474 26.533 29.739 26.223L32.36 23.15C34.062 21.161 35 18.622 35 16.001V15.001C35 11.973 33.732 9.047 31.521 6.975C29.279 4.873 26.345 3.83 23.269 4.025C17.51 4.399 12.999 9.371 12.999 15.344V16C12.999 18.621 13.937 21.16 15.638 23.148L18.26 26.222C18.525 26.532 18.515 26.868 18.485 27.041C18.456 27.214 18.352 27.534 17.999 27.737L11.572 31.43C8.134 33.383 6 37.051 6 41V44H8V41.002C8 37.771 9.747 34.77 12.565 33.168L18.997 29.473C19.772 29.027 20.305 28.265 20.457 27.383C20.609 26.501 20.364 25.605 19.783 24.924L17.16 21.849C15.767 20.222 15.001 18.145 15.001 16V15.343C15.001 10.42 18.69 6.325 23.399 6.02C25.925 5.848 28.319 6.713 30.154 8.433C31.99 10.154 33.001 12.486 33.001 15V16C33.001 18.145 32.234 20.222 30.841 21.85L28.219 24.924C27.638 25.605 27.393 26.501 27.545 27.383C27.697 28.265 28.231 29.027 29.007 29.473L30.659 30.423C31.349 29.996 32.091 29.644 32.88 29.393ZM40 23.254V34.506C38.92 33.525 37.492 32.922 35.922 32.922C32.57 32.922 29.844 35.648 29.844 39C29.844 42.352 32.571 45.078 35.922 45.078C39.273 45.077 42 42.351 42 39V26.718L46.035 29.047L47.035 27.315L40 23.254ZM35.922 43.077C33.673 43.077 31.844 41.248 31.844 38.999C31.844 36.75 33.674 34.921 35.922 34.921C38.17 34.922 40 36.751 40 39C40 41.249 38.17 43.077 35.922 43.077Z"
                                      fill="#A7A7A7"
                                    />
                                  </g>
                                )}

                                <circle
                                  fill={
                                    uri
                                      ? getTypeFromUri(uri) === 'genre' ||
                                        getTypeFromUri(uri) === 'decade'
                                        ? getColorForGenreUri(uri) || '#535353'
                                        : `url(#${image || 'no-artist-image'})`
                                      : '#535353'
                                  }
                                  cx={52}
                                  cy={52}
                                  r="52"
                                />

                                {getTypeFromUri(uri) === 'genre' && (
                                  <>
                                    <circle
                                      fill={`url(#${uri}-gradient)`}
                                      cx={52}
                                      cy={52}
                                      r="52"
                                    />
                                    <defs>
                                      <linearGradient
                                        id={`${uri}-gradient`}
                                        x1="-52"
                                        y1="13.8235"
                                        x2="-24.3529"
                                        y2="117.824"
                                        gradientUnits="userSpaceOnUse"
                                      >
                                        <stop stopOpacity="0.25" />
                                        <stop offset="1" stopOpacity="0.01" />
                                      </linearGradient>
                                    </defs>
                                  </>
                                )}

                                {id === 'add-more' && (
                                  <g transform="translate(43, 43)">
                                    <path
                                      d="M18 8H10V0H8V8H0V10H8V18H10V10H18V8Z"
                                      fill="white"
                                    />
                                  </g>
                                )}

                                <foreignObject y="112" width="104" height="40">
                                  <p className="Playlist__seed-title">
                                    {getTypeFromUri(uri) === 'genre'
                                      ? name?.replace(
                                          /((?:^|\b).)/g,
                                          (firstLetter) =>
                                            firstLetter.toUpperCase()
                                        )
                                      : name
                                          ?.replace(/^(00|10)s$/, '20$1')
                                          ?.replace(
                                            /^(90|80|70|60|50)s$/,
                                            '19$1'
                                          )}
                                  </p>
                                </foreignObject>
                              </g>
                            </g>

                            {image && (
                              <defs>
                                <pattern
                                  id={image}
                                  width="1"
                                  height="1"
                                  patternContentUnits="objectBoundingBox"
                                >
                                  <image
                                    width="208"
                                    height="208"
                                    xlinkHref={image}
                                    transform="scale(.0048)"
                                    preserveAspectRatio="xMidYMin slice"
                                  />
                                </pattern>
                              </defs>
                            )}
                          </svg>
                        </Flipped>
                      ))}
                  </div>

                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="120"
                    height="32"
                    fill="none"
                    viewBox="0 0 120 32"
                    className="Playlist__seeds-preview-songs"
                    onClick={() => setIsPreviewSongsPaneVisible(true)}
                  >
                    <text
                      fill="#fff"
                      style={{ whiteSpace: 'pre' }}
                      fontSize="13"
                      fontWeight="bold"
                      letterSpacing="0em"
                    >
                      <tspan x="16.455" y="20.258">
                        Preview songs
                      </tspan>
                    </text>

                    <rect
                      x="1"
                      y="1"
                      width="118"
                      height="30"
                      rx="16"
                      stroke="#727272"
                      strokeWidth="1"
                    />

                    {/* <path
                    fill="#727272"
                    d="M16 1h88v-2H16v2zm88 30H16v2h88v-2zm-88 0C7.716 31 1 24.284 1 16h-2c0 9.389 7.611 17 17 17v-2zm103-15c0 8.284-6.716 15-15 15v2c9.389 0 17-7.611 17-17h-2zM104 1c8.284 0 15 6.716 15 15h2c0-9.389-7.611-17-17-17v2zM16-1C6.611-1-1 6.611-1 16h2C1 7.716 7.716 1 16 1v-2z"
                  ></path> */}
                  </svg>
                </div>
              </Flipper>
            )}

            {/* {isOwnedByCurrentUser && (
          <Link
            to={`/playlist/${playlistId}/add`}
            style={{
              textDecoration: 'none',
              marginBottom: '-8px',
              marginTop: '8px',
              display: 'block',
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 375 64"
              style={{ display: 'block' }}
            >
              <text
                fill="#fff"
                style={{ whiteSpace: 'pre' }}
                fontSize="16"
                letterSpacing="0em"
              >
                <tspan x="76" y="37.24">
                  Add songs
                </tspan>
              </text>
              <path fill="#282828" d="M16 8H64V56H16z"></path>
              <path
                fill="#BDBDBD"
                d="M41 31v-9h-.999v9H31v1h9.001v9H41v-9h9v-1h-9z"
              ></path>
            </svg>
          </Link>
        )} */}

            {/* <div className="Playlist__free-track-cloud">
          {rows.slice(0, 10).map((row, i, all) => (
            <span className="Playlist__free-track-cloud-track">
              <span className="Playlist__free-track-cloud-track-name">
                {row?.track?.artists?.[0]?.name}
              </span>{' '}
              <span className="Playlist__free-track-cloud-artist-name">
                {row?.track?.name}
              </span>
              {i !== all.length - 1 ? ` • ` : ''}
            </span>
          ))}
        </div> */}

            {!isDynamicPlaylist ? (
              onDemand ? (
                <div className="Playlist__tracklist-wrapper">
                  <Flipper
                    flipKey={rows.map(({ uid }) => uid).join('')}
                    spring="noWobble"
                  >
                    {rows.map(
                      (
                        {
                          track: {
                            uri,
                            id,
                            name,
                            album: { images = [] } = {},
                            artists,
                            sortValue,
                          } = {},
                          uid = uri,
                          metadata: { source } = {},
                        },
                        index
                      ) => {
                        const isAdded = addedStateByUri[uri];
                        return (
                          <React.Fragment key={uid}>
                            <Flipped
                              key={uid}
                              flipId={uid}
                              onAppear={onAppear}
                              onExit={onExit}
                            >
                              <div className="Playlist__track-row-wrapper">
                                <TrackRowActionsWrapper
                                  onAddRemove={() => {
                                    setTimeout(
                                      () =>
                                        props.toggleSavedStateForUri(
                                          uri,
                                          playlistUri
                                        ),
                                      1000
                                    );
                                  }}
                                  isSaved={isAdded}
                                >
                                  <TrackRow
                                    uri={uri}
                                    title={name}
                                    subtitle={artists
                                      .map(({ name }) => name)
                                      .join(', ')}
                                    imageUrl={getImagesUrlBySize(images, 54)}
                                    onClick={() =>
                                      onDemand
                                        ? props.playTracksFromUri({
                                            trackUri: uri,
                                            rows,
                                            index,
                                            contextUri: playlistUri,
                                          })
                                        : isPreviewPlayerPlaying &&
                                          previewUri === uri
                                        ? props.pausePreview(uri)
                                        : props.playPreview(uri)
                                    }
                                    isPlaying={
                                      uri &&
                                      uri ===
                                        (currentRow &&
                                          currentRow.track &&
                                          currentRow.track.uri) &&
                                      isActiveContext &&
                                      isSameFeatureIdentifier
                                    }
                                    feedback={trackFeedbackByUri[uri]}
                                    onToggleFeedbackState={() => {
                                      // props.feedbackTrack(uri, playlistUri, 0);
                                    }}
                                    onActionButtonClick={() =>
                                      props.openContextMenu(uri, playlistUri)
                                    }
                                    onDemand={onDemand}
                                    shouldScrollIntoView={
                                      uri === trackUriToScrollIntoView
                                    }
                                    sortValue={sortValue}
                                  />
                                </TrackRowActionsWrapper>
                              </div>
                            </Flipped>
                          </React.Fragment>
                        );
                      }
                    )}
                  </Flipper>
                </div>
              ) : (
                <>
                  {!!rowsAddedByUser?.length && (
                    <AutoSizingTrackCloud
                      rows={rowsAddedByUser}
                      title={rowsAddedBySpotify && 'You added'}
                    />
                  )}
                  {!!rowsAddedBySpotify?.length && (
                    <AutoSizingTrackCloud
                      rows={rowsAddedBySpotify}
                      title={'We added'}
                    />
                  )}
                </>
              )
            ) : null}
          </div>
        </div>
      </Scroller>

      {isDynamicPlaylist && !!rows?.length && (
        <div
          className={`Playlist__preview-tracks-pane ${
            isPreviewSongsPaneVisible
              ? 'Playlist__preview-tracks-pane--is-visible'
              : ''
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 375 56"
            onClick={() => setIsPreviewSongsPaneVisible(false)}
          >
            <path
              fill="#121212"
              d="M0 8a8 8 0 018-8h359a8 8 0 018 8v40a8 8 0 01-8 8H8a8 8 0 01-8-8V8z"
            ></path>
            <rect fill="#000" fillOpacity="0.001" width="70" height="56" />

            <rect
              fill="#000"
              fillOpacity="0.001"
              x="70"
              width="300"
              height="56"
            />

            <path
              fill="#fff"
              d="M44.355 20.353l-.708-.706L36 27.293l-7.647-7.646-.706.706L35.294 28l-7.646 7.646.706.708L36 28.707l7.646 7.647.708-.708L36.708 28l7.647-7.647z"
            ></path>
          </svg>

          <Scroller
            className="Playlist__preview-tracks-pane-content"
            deps={isPreviewSongsPaneVisible}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 375 80"
            >
              <g transform="translate(0, -56)">
                <path
                  fill="#242424"
                  d="M0 16C0 8.458 0 4.686 2.343 2.343S8.458 0 16 0h343c7.542 0 11.314 0 13.657 2.343S375 8.458 375 16v752H0V16z"
                ></path>
                <path
                  fill="#121212"
                  d="M0 0H375V34H0z"
                  transform="translate(0 734)"
                ></path>
                <path
                  fill="#121212"
                  d="M0 0H375V678H0z"
                  transform="translate(0 56)"
                ></path>
                <path fill="#121212" d="M0 0H375V56H0z"></path>
                <text
                  fill="#fff"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="32"
                  fontWeight="bold"
                  letterSpacing="-.04em"
                >
                  <tspan x="16" y="94.48">
                    {playlistName}
                  </tspan>
                </text>
                <text
                  fill="#A7A7A7"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="16"
                  fontWeight="450"
                  letterSpacing="0em"
                >
                  <tspan x="16" y="121.24">
                    Songs will refresh every day.
                  </tspan>
                </text>
              </g>
            </svg>
            <div className="Playlist__tracklist-wrapper">
              <Flipper
                flipKey={rows.map(({ uid }) => uid).join('')}
                spring="noWobble"
              >
                {rows.map(
                  (
                    {
                      track: {
                        uri,
                        id,
                        name,
                        album: { images = [] } = {},
                        artists,
                        sortValue,
                      } = {},
                      uid = uri,
                      metadata: { source } = {},
                    },
                    index
                  ) => {
                    return (
                      <React.Fragment key={uid}>
                        <Flipped
                          key={uid}
                          flipId={uid}
                          onAppear={onAppear}
                          onExit={onExit}
                        >
                          <div className="Playlist__track-row-wrapper">
                            <TrackRow
                              uri={uri}
                              title={name}
                              subtitle={artists
                                .map(({ name }) => name)
                                .join(', ')}
                              imageUrl={getImagesUrlBySize(images, 54)}
                              onClick={() =>
                                onDemand
                                  ? props.playTracksFromUri({
                                      trackUri: uri,
                                      rows,
                                      index,
                                      contextUri: playlistUri,
                                    })
                                  : isPreviewPlayerPlaying && previewUri === uri
                                  ? props.pausePreview(uri)
                                  : props.playPreview(uri)
                              }
                              isPlaying={
                                uri &&
                                uri ===
                                  (currentRow &&
                                    currentRow.track &&
                                    currentRow.track.uri) &&
                                isActiveContext &&
                                isSameFeatureIdentifier
                              }
                              onToggleFeedbackState={() => {
                                // props.feedbackTrack(uri, playlistUri, 0);
                              }}
                              onActionButtonClick={() =>
                                props.openContextMenu(uri, playlistUri)
                              }
                              onDemand={onDemand}
                              sortValue={sortValue}
                            />
                          </div>
                        </Flipped>
                      </React.Fragment>
                    );
                  }
                )}
              </Flipper>
            </div>
          </Scroller>
        </div>
      )}
    </>
  );
}

export default connect(
  (
    {
      playlistsById,
      currentRow,
      contextUri,
      addedStateByUri,
      lastAddedTrackUri,
      lastRemovedTrackUri,
      trackRowRecommendationsByContextUri,
      trackFeedbackByUri,
      canvasesByEntityUri,
      playerState: { paused, shuffle } = {},
      contextPlayerState,
      nextRows,
      onDemand,
      preview: {
        uri: previewUri,
        fileUri,
        isPlaying: isPreviewPlayerPlaying,
        key: previewKey,
      },
      featureIdentifier,
    },
    { match: { params: { id: playlistId = '', section = '' } = {} } = {} }
  ) => {
    const {
      name,
      description,
      ownerId,
      ownerDisplayName,
      ownerImageUrl,
      followersTotal,
      numTracks,
      rows = [],
      rowsBySteering = {},
      steer,
      color,
      imageUrl,
      isFollowing,
      isAutoUpdateEnabled,
      isOwnedByCurrentUser,
      isOwnedBySpotify,
      recommendedPlaylistUris,
      sort,
      seeds,
    } = playlistsById[playlistId] || {};

    const potentiallySteeredRows = rowsBySteering[steer] || rows;
    const playlistUri = `spotify:playlist:${playlistId}`;
    const isActiveContext = playlistUri === contextUri;
    return {
      playlistId,
      section,
      name,
      description,
      ownerId,
      ownerDisplayName,
      ownerImageUrl,
      followersTotal,
      numTracks: numTracks || potentiallySteeredRows?.length || null,
      rows: potentiallySteeredRows,
      color,
      imageUrl,
      isFollowing,
      currentRow,
      contextUri,
      isAutoUpdateEnabled,
      isOwnedByCurrentUser,
      isOwnedBySpotify,
      addedStateByUri,
      lastAddedTrackUri,
      lastRemovedTrackUri,
      trackRowRecommendations: trackRowRecommendationsByContextUri[playlistUri],
      playlistUri,
      trackFeedbackByUri,
      recommendedPlaylists:
        recommendedPlaylistUris?.map(
          (recommendedPlaylistUri) =>
            playlistsById[recommendedPlaylistUri.substr(-22)]
        ) || [],
      isPlaying: !paused && playlistUri === contextUri,
      isSameFeatureIdentifier:
        featureIdentifier ===
        contextPlayerState?.play_origin?.feature_identifier,
      isActiveContext,
      canvas: canvasesByEntityUri[currentRow?.track?.uri],
      onDemand,
      isPreviewPlayerPlaying,
      previewUri,
      sort,
      seeds,
    };
  },
  actions
)(React.memo(Playlist, isEqual));

const AutoSizingTrackCloud = React.memo(function AutoSizingTrackCloud({
  title,
  rows,
}) {
  const lineCount = 3;
  const lineHeight = 24;

  const autoMeasuringTrackCloudRef = useRef(null);

  const render = (rows, andMore) => `<div class="Playlist__free-track-cloud">
  ${rows
    .slice(0, 10)
    .map(
      (row, i, all) =>
        `<span class="Playlist__free-track-cloud-track">
          <span class="Playlist__free-track-cloud-artist-name">
            ${escapeHTML(row?.track?.artists?.[0]?.name)}
          </span>
          <span class="Playlist__free-track-cloud-track-name">
            ${escapeHTML(row?.track?.name)}
            ${i !== all.length - 1 || andMore ? ` • ` : ''}
          </span>

          ${
            i === all.length - 1 && andMore
              ? `<span class="Playlist__free-track-cloud-and-more">and more</span>`
              : ''
          }
        </span>`
    )
    .join('\n')}
</div>`;

  useEffect(() => {
    if (!rows.length) return;

    const rowsCopy = rows.slice(5);
    const rowsToRender = rows.slice(0, 5);

    let row;
    let hasDiscardedRows = false;
    do {
      autoMeasuringTrackCloudRef.current.innerHTML = render(
        rowsToRender,
        hasDiscardedRows
      );

      if (
        autoMeasuringTrackCloudRef.current?.offsetHeight <=
        lineCount * lineHeight
      ) {
        if (row) rowsToRender.push(row);
      } else {
        rowsToRender.shift();
        hasDiscardedRows = true;
      }

      if (!rowsCopy.length) {
        autoMeasuringTrackCloudRef.current.innerHTML = render(
          rowsToRender.slice(0, hasDiscardedRows ? -1 : Infinity),
          hasDiscardedRows
        );
      }
    } while ((row = rowsCopy.shift()));
  }, [rows]);

  return (
    <div className="AutoMeasuringTrackCloud Playlist__free-track-cloud-wrapper">
      {title && (
        <div className="PlaylistHeader__track-cloud-title">{title}</div>
      )}
      <div ref={autoMeasuringTrackCloudRef} />
    </div>
  );
});

const AutoSizingSeeds = React.memo(function AutoSizingSeeds({
  seeds,
  history,
  playlistId,
}) {
  const lineCount = 1;
  const lineHeight = 24;

  const autoSizingSeedsRef = useRef(null);

  let nonTrackSeeds =
    seeds?.filter((seed) => getTypeFromUri(seed?.uri) !== 'track') || [];

  if (seeds && !nonTrackSeeds?.length) {
    const songCount =
      seeds?.filter((seed) => getTypeFromUri(seed?.uri) === 'track')?.length ||
      0;
    nonTrackSeeds = [
      {
        name: `${songCount} song${songCount === 1 ? '' : 's'}`,
      },
    ];
  }

  const render = (seeds, andMore) =>
    `<div class="PlaylistHeader__tuning">
      ${seeds
        ?.map((seed, i) => {
          const imageUrl = getImagesUrlBySize(
            seed?.images || seed?.album?.images || [{ url: seed?.image }],
            20
          );

          return (
            imageUrl &&
            `<span
                class="PlaylistHeader__tuning-facepile-image"
                style="background-image: url(${escapeHTML(imageUrl)})"
              ></span>`
          );
        })
        .filter(Boolean)}
    <div class="PlaylistHeader__tuning-text">
      ${escapeHTML(
        seeds
          .map((seed) =>
            getTypeFromUri(seed?.uri) === 'genre'
              ? seed?.name?.replace(/((?:^|\b).)/g, (firstLetter) =>
                  firstLetter.toUpperCase()
                )
              : seed?.name
          )
          .filter(Boolean)
          .join(' • ')
      )}${andMore ? ' and more' : ''}
    </div>

    <svg width="25" height="8" viewBox="0 0 25 8" fill="none" xmlns="http://www.w3.org/2000/svg" class="PlaylistHeader__tuning-arrow">
      <path d="M4.92625 8V6.7H1.86625V5.06H4.63625V3.83H1.86625V2.21H4.92625V0.909999H0.48625V8H4.92625ZM8.65727 6.72V2.19H9.76727C10.9273 2.19 11.8873 2.92 11.8873 4.46C11.8873 6 10.9173 6.72 9.75727 6.72H8.65727ZM9.80727 8C11.8273 8 13.3173 6.7 13.3173 4.46C13.3173 2.22 11.8373 0.909999 9.81727 0.909999H7.27727V8H9.80727ZM16.982 8V0.909999H15.582V8H16.982ZM24.8386 2.22V0.909999H18.9686V2.22H21.2086V8H22.5986V2.22H24.8386Z" fill="white"/>
    </svg>
  </div>`;

  useEffect(() => {
    if (!nonTrackSeeds.length) return;

    const seedsToRender = nonTrackSeeds.concat();

    let hasDiscardedSeeds = false;
    do {
      const html = render(seedsToRender, hasDiscardedSeeds);
      autoSizingSeedsRef.current.innerHTML = html;

      if (
        autoSizingSeedsRef.current?.querySelector(
          '.PlaylistHeader__tuning-text'
        )?.offsetHeight <=
        lineCount * lineHeight
      ) {
        break;
      } else {
        hasDiscardedSeeds = true;
      }
    } while (seedsToRender.shift());
  }, [nonTrackSeeds]);

  return nonTrackSeeds.length ? (
    <div
      className="PlaylistHeader__tuning-wrapper"
      onClick={() =>
        history.push(
          `/edit/${
            nonTrackSeeds.length ? 'dynamic-playlist' : 'playlist'
          }/${playlistId}`
        )
      }
      ref={autoSizingSeedsRef}
    />
  ) : null;
});

const escapeHTML = (str) =>
  str.replace(
    /[&<>'"]/g,
    (tag) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;',
      }[tag] || tag)
  );

const LoadMoreSongs = React.memo(
  // @ts-ignore
  connect(
    null,
    actions
  )(function LoadMoreSongs(props) {
    const { scroller, playlistId, loadNextChunk } = props;

    const [intersectionRatio, setIntersectionRatio] = useState(0);
    const intersectionRef = useRef(null);

    const shouldLoadMore = intersectionRatio >= 0.95;
    const [isLoading, setIsLoading] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
      (async () => {
        if (shouldLoadMore && !isScrolling && !isDragging && !isLoading) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          setIsLoading(true);
          loadNextChunk(playlistId).then(() => setIsLoading(false));
        }
      })();
    }, [isScrolling, isDragging, shouldLoadMore]);

    useEffect(() => {
      if (!scroller) return;

      const viewport = scroller.props.viewport;

      const updateIntersectionRatio = () => {
        var loadMoreRect = intersectionRef.current.getBoundingClientRect();
        var viewportRect = viewport.getBoundingClientRect();

        setIntersectionRatio(
          Math.max(viewportRect.bottom - loadMoreRect.top, 0) /
            loadMoreRect.height
        );
      };

      const onViewportPointerDown = () => {
        setIsDragging(true);
      };

      const onDocumentPointerUp = () => {
        updateIntersectionRatio();
        setIsDragging(false);
      };

      let scrollTimeout;
      const onViewportScroll = () => {
        setIsScrolling(true);
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          updateIntersectionRatio();
          setIsScrolling(false);
        }, 66);

        updateIntersectionRatio();
      };

      viewport.addEventListener('scroll', onViewportScroll, false);
      viewport.addEventListener('pointerdown', onViewportPointerDown, false);
      document.addEventListener('pointerup', onDocumentPointerUp, false);
      viewport.addEventListener('touchstart', onViewportPointerDown, false);
      document.addEventListener('touchend', onDocumentPointerUp, false);

      return () => {
        viewport.removeEventListener('scroll', onViewportScroll, false);
        viewport.removeEventListener(
          'pointerdown',
          onViewportPointerDown,
          false
        );
        document.removeEventListener('pointerup', onDocumentPointerUp, false);
        viewport.removeEventListener(
          'touchstart',
          onViewportPointerDown,
          false
        );
        document.removeEventListener('touchend', onDocumentPointerUp, false);
        clearTimeout(scrollTimeout);
      };
    }, [scroller]);

    return (
      <div ref={intersectionRef}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          fill="none"
          viewBox="0 0 375 64"
          style={{
            transform: `translate3d(0, ${
              (Math.max(intersectionRatio - 0.2, 0) || 0) * 24 - 32
            }px, 0)`,
            willChange: 'transform',
          }}
        >
          <g
            style={{
              opacity: shouldLoadMore ? 1 : 0.7,
              willChange: 'opacity',
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            {isLoading ? (
              <foreignObject x="160" y="36" width="56" height="12">
                <LoadingIcon />
              </foreignObject>
            ) : (
              <g>
                <g
                  style={{
                    transform: `rotate(${shouldLoadMore ? 180 : 0}deg)`,
                    willChange: 'transform',
                    transition: 'transform 0.3s ease-in-out',
                    transformOrigin: '19.50649881362915px 44.38348388671875px',
                  }}
                >
                  <path
                    fill="#fff"
                    d="M19.498 50.767l4.887-5.664-.757-.653-3.633 4.21V38h-.99v10.66l-3.62-4.21-.757.652 4.87 5.665z"
                  ></path>
                </g>

                <text
                  fill="#fff"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="10"
                  fontWeight="bold"
                  letterSpacing=".1em"
                >
                  <tspan x="96.032" y="47.275">
                    {shouldLoadMore
                      ? 'RELEASE TO LOAD MORE SONGS'
                      : 'PULL TO LOAD MORE SONGS'}
                  </tspan>
                </text>
              </g>
            )}
          </g>
        </svg>
      </div>
    );
  })
);
